import { DEFAULT_EXPORT_ROW_NUM, FILTER_KEYS } from '../consts';
import useFilters from '../hooks/useFilters';
import { flattenOrderForExport } from '../utils';
import { Listbox, Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { getUserInfo } from 'api/requests/core/users';
import { getExportedOrders } from 'api/requests/giftCard/giftCard';
import { DropdownIcon, ExportIcon } from 'assets/svg';
import { useMemo } from 'react';
import { Role } from 'shared/consts/role';
import { queryKeyBuilder } from 'shared/query';
import * as XLSX from 'xlsx';

function ExportDropdown() {
  const urlQueryParams = useFilters();

  const fileName = 'giftcards';
  const fileExtension = '.xlsx';

  const { data: userData } = useQuery({
    queryKey: queryKeyBuilder.currentUser(),
    queryFn: getUserInfo,
  });

  const canExport = useMemo(() => {
    const userRoles = userData?.roles ?? [];
    return userRoles.includes(Role.GC_Export);
  }, [userData]);

  const { data: exportedOrdersData } = useQuery({
    queryKey: queryKeyBuilder.giftCardsToExport(urlQueryParams),
    queryFn: () => getExportedOrders(urlQueryParams),
    enabled: canExport,
  });

  const chunkNumber = Math.ceil((exportedOrdersData?.total ?? 1) / DEFAULT_EXPORT_ROW_NUM);

  const getOrdersApiCall = async (pageNum: number) => {
    const exportParams = {
      ...urlQueryParams,
      [FILTER_KEYS.pageNum]: pageNum.toString(),
      [FILTER_KEYS.pageSize]: DEFAULT_EXPORT_ROW_NUM.toString(),
    };
    const response = await getExportedOrders(exportParams);
    return response.orders;
  };

  const exportToCSV = async (fileName: string, pageNum: number) => {
    try {
      const apiData = await getOrdersApiCall(pageNum);
      const currentDate = new Date().toJSON();
      const worksheet = XLSX.utils.json_to_sheet(apiData.map(flattenOrderForExport));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'data');
      XLSX.writeFile(workbook, `${fileName}-${currentDate}${fileExtension}`, { compression: true });
    } catch (error) {
      console.error('Error exporting to CSV:', error);
    }
  };

  return chunkNumber <= 1 ? (
    <button
      className="group text-white bg-palette-burgundy flex px-4 py-3 items-center rounded-full whitespace-nowrap"
      onClick={() => exportToCSV(fileName, 1)}
    >
      <ExportIcon className="transition-transform group-hover:scale-110 w-6 h-6 flex-none" />
      <span className="ml-2 mr-4 tracking-tight text-base font-semibold">Export Results</span>
    </button>
  ) : (
    <Listbox as="div" className={`${canExport ? 'visible' : 'invisible'} relative max-w-56`}>
      {({ open }) => (
        <>
          <Listbox.Button className="group text-white bg-palette-burgundy flex px-4 py-3 items-center rounded-full">
            <ExportIcon className="transition-transform group-hover:scale-110 w-6 h-6" />
            <span className="ml-2 mr-4 tracking-tight text-base font-semibold">Export Results</span>
            <DropdownIcon className={`transition-transform ${open ? 'rotate-180' : 'rotate-0'} w-3 h-3`} />
          </Listbox.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            className="w-full"
          >
            <Listbox.Options className="bg-white flex flex-col rounded-lg px-1.5 pt-5 pb-4 absolute right-0 mt-1 z-20 w-max max-w-80">
              <span className="text-xs text-palette-secondary tracking-tight px-4 mb-4">
                Due to the size of your data set, it couldn't be exported as a single file. We have generated multiple
                files for you to download.
              </span>
              <div className="max-h-52 overflow-auto scrollbar flex flex-col gap-0.5 w-full max-w-full px-1.5">
                {new Array(chunkNumber).fill(true).map((_, idx) => (
                  <Listbox.Option
                    className="font-normal  rounded transition-colors text-sm tracking-tight flex items-center text-palette-secondary px-2.5 py-2.5 w-full max-w-full cursor-pointer hover:bg-palette-pinkish-gray"
                    key={idx}
                    value={idx}
                    onClick={() => exportToCSV(fileName, idx + 1)}
                  >
                    <span className="text-sm underline truncate break-words">{`File ${idx + 1} (Rows ${
                      idx * DEFAULT_EXPORT_ROW_NUM
                    }-${(idx + 1) * DEFAULT_EXPORT_ROW_NUM})`}</span>
                  </Listbox.Option>
                ))}
              </div>
            </Listbox.Options>
          </Transition>
        </>
      )}
    </Listbox>
  );
}

export default ExportDropdown;
